/**
 * Render an ISO timestamp as a short human-relative string for UI labels.
 *
 * Examples (now = 2026-04-29T18:38:00Z):
 *   - "2026-04-29T19:00:00Z"  → "in 22m"
 *   - "2026-04-30T00:00:00Z"  → "in 5h 22m"
 *   - "2026-04-30T18:38:00Z"  → "tomorrow"
 *   - "2026-05-15T00:00:00Z"  → "in 16d"
 *
 * Returns `null` for invalid input or past timestamps (clock skew, stale
 * server data). Callers should omit the surrounding label entirely when
 * this returns null — phrases like "Renews now" / "Resets now" carry no
 * information and look like a UI bug.
 */
export function humanizeRelative(iso: string, now: Date = new Date()): string | null {
  const target = new Date(iso);
  if (isNaN(target.getTime())) return null;

  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return null;

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `in ${Math.max(1, minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const remainderMin = minutes % 60;
  if (hours < 24) {
    return remainderMin > 0 ? `in ${hours}h ${remainderMin}m` : `in ${hours}h`;
  }

  const days = Math.floor(hours / 24);
  if (days === 1) return 'tomorrow';
  return `in ${days}d`;
}
